import { type FC, useEffect, useState } from 'react';
import {
  ForgeButton,
  ForgeCircularProgress,
  ForgeIcon,
  ForgeIconButton,
  ForgeInlineMessage,
  ForgeSelect,
} from '@tylertech/forge-react';

import './index.scss';
import isEmpty from 'lodash/isEmpty';
import { type FilterMetadataCollection } from 'common/types/filterMetadata';
import { type AttachmentConfiguration, type TableAttachment } from '../../types/attachment';
import { ATTACHMENT_ICONS, DEFAULT_ATTACHMENT_CONFIG } from '../../constants';
import { I18n } from 'common/i18n';
import {
  generateDefaultPdfConfig,
  getExportFormatOptions,
  getTableOptions,
  joinTableNames,
  getRowCountForTable,
} from './helpers';
import { createDefaultPDFFullReport } from '../../helpers';
import { type ScheduleSendValues } from '../../types/scheduleSend';
import { PdfConfiguration } from './PdfConfiguration';
import isUndefined from 'lodash/fp/isUndefined';
import { isTablePrintingImprovementForScheduledSendEnabled } from 'common/visualizations/helpers/VifSelectors';

const scope = 'shared.components.schedule_send_report.modal.attachment_section';
const MAX_PDF_ATTACHMENT_ROW_COUNT = 10000;

export interface AttachmentSectionProps {
  tableList: TableAttachment[] | void;
  attachmentConfigs: AttachmentConfiguration[];
  updateScheduleSendValue: <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => void;
  filterWhereClauses?: Record<string, string | null>;
  filterMetadata?: FilterMetadataCollection;
}

const AttachmentSection: FC<AttachmentSectionProps> = (props) => {
  const { tableList, attachmentConfigs, updateScheduleSendValue, filterWhereClauses, filterMetadata } = props;
  const [multiHierarchyTable, setMultiHierarchyTable] = useState<string[]>([]);
  const [rowCountCache, setRowCountCache] = useState<Record<string, number>>({});
  const [loadingRowCount, setLoadingRowCount] = useState<number | null>(null);

  const handleAddAttachment = () => {
    updateScheduleSendValue('attachmentConfigs', [...attachmentConfigs, createDefaultPDFFullReport()]);
  };

  useEffect(() => {
    const tableMap = new Map(tableList?.map((table) => [table.componentUuid, table]) ?? []);
    const namesSet = new Set<string>();
    for (const config of attachmentConfigs) {
      const { componentUuid, value } = config;
      const tableInfo = (componentUuid && tableMap.get(componentUuid)) || tableMap.get(value);

      if (tableInfo?.withMultipleHierarchy) {
        namesSet.add(tableInfo.title);
      }
    }

    const multipleHierarchyNames = Array.from(namesSet);
    setMultiHierarchyTable(multipleHierarchyNames);
  }, [attachmentConfigs, tableList]);

  const handleRemoveAttachment = (index: number) => {
    const newAttachmentConfigs = attachmentConfigs.filter((_, i) => i !== index);
    updateScheduleSendValue('attachmentConfigs', newAttachmentConfigs);
  };

  const onTableChange = async (index: number, event: CustomEvent) => {
    const value = event.detail as string;
    const newAttachmentConfigs = [...attachmentConfigs];
    newAttachmentConfigs[index].value = value;

    const selectedTable = tableList?.find((table) => table.componentUuid === value);

    let rowCount = 0;

    if (!isUndefined(selectedTable) && value !== 'full-report') {
      setLoadingRowCount(index);
      try {
        rowCount = await getRowCountForTable(
          selectedTable,
          rowCountCache,
          setRowCountCache,
          filterWhereClauses,
        );
      } finally {
        setLoadingRowCount(null);
      }
    }

    // we only support pdf format type for full report attachment
    if (value === 'full-report') {
      newAttachmentConfigs[index].format = 'pdf';
    } else {
      // For table exports, determine the default format based on row count
      const shouldDefaultToCsv = rowCount >= MAX_PDF_ATTACHMENT_ROW_COUNT;
      newAttachmentConfigs[index].format = shouldDefaultToCsv ? 'csv' : 'pdf';
      newAttachmentConfigs[index].isTableSupportedForPdfExport = !shouldDefaultToCsv;
    }

    if (value === 'full-report') {
      newAttachmentConfigs[index].type = 'full_report';
    } else {
      newAttachmentConfigs[index].type = 'table_export';
      newAttachmentConfigs[index].componentUuid = selectedTable?.componentUuid;
    }

    // Generate PDF config for table exports with PDF format
    if (newAttachmentConfigs[index].type === 'table_export' && newAttachmentConfigs[index].format === 'pdf') {
      const isTableWithGrouping = Boolean(selectedTable?.withGrouping);
      newAttachmentConfigs[index].pdfOptions = generateDefaultPdfConfig(isTableWithGrouping);
    }

    updateScheduleSendValue('attachmentConfigs', newAttachmentConfigs);
  };

  const onFormatChange = (index: number, event: CustomEvent) => {
    const value = event.detail as string;
    const newAttachmentConfigs = [...attachmentConfigs];
    newAttachmentConfigs[index].format = value;

    if (value !== 'pdf' && newAttachmentConfigs[index].pdfOptions) {
      delete newAttachmentConfigs[index].pdfOptions;
    } else if (value === 'pdf' && !newAttachmentConfigs[index].pdfOptions) {
      const isTableWithGrouping = Boolean(
        tableList?.find((table) => table.componentUuid === attachmentConfigs[index].value)?.withGrouping,
      );
      newAttachmentConfigs[index].pdfOptions = generateDefaultPdfConfig(isTableWithGrouping);
    }

    updateScheduleSendValue('attachmentConfigs', newAttachmentConfigs);
  };

  const attachmentOptions = (index: number, totalAttachments: number) => {
    const tableCurrentValue = isEmpty(attachmentConfigs[index].value)
      ? DEFAULT_ATTACHMENT_CONFIG.value
      : attachmentConfigs[index].value;

    const formatCurrentValue = isEmpty(attachmentConfigs[index].format)
      ? DEFAULT_ATTACHMENT_CONFIG.format
      : attachmentConfigs[index].format;

    const isTableWithGrouping = Boolean(
      tableList?.find((table) => table.componentUuid === tableCurrentValue)?.withGrouping,
    );

    return (
      <div className="attachment-select-dropdrowns">
        <ForgeSelect
          className="attachment-table-list"
          data-testid="attachment-table-select"
          value={tableCurrentValue}
          options={getTableOptions(tableList)}
          syncPopupWidth
          on-change={(event: CustomEvent) => onTableChange(index, event)}
        >
          <ForgeIcon slot="start" name={ATTACHMENT_ICONS[attachmentConfigs[index].type]} />
        </ForgeSelect>

        <ForgeSelect
          className="attachment-format-list"
          data-testid="attachment-format-select"
          value={formatCurrentValue}
          options={getExportFormatOptions(attachmentConfigs[index])}
          disabled={tableCurrentValue === 'full-report' || loadingRowCount === index}
          syncPopupWidth
          on-change={(event: CustomEvent) => onFormatChange(index, event)}
        >
          {loadingRowCount === index && (
            <ForgeCircularProgress
              slot="end"
              className="row-count-loading-spinner"
              data-testid="row-count-loading-spinner"
            />
          )}
        </ForgeSelect>

        <PdfConfiguration
          index={index}
          tableCurrentValue={tableCurrentValue}
          formatCurrentValue={formatCurrentValue}
          attachmentConfigs={attachmentConfigs}
          updateScheduleSendValue={updateScheduleSendValue}
          isTableWithGrouping={isTableWithGrouping}
          {...(isTablePrintingImprovementForScheduledSendEnabled() && { filterMetadata })}
        />

        <ForgeIconButton
          densityLevel={3}
          slot="trailing"
          data-testid="remove-attachment-btn"
          className="remove-attachment"
          hidden={totalAttachments <= 1}
          dense
          onClick={() => {
            handleRemoveAttachment(index);
          }}
        >
          <ForgeIcon slot="end" name="close" />
        </ForgeIconButton>
      </div>
    );
  };

  return (
    <div className="attachment-section" data-testid="attachment-section">
      <div className="contents">
        <h1 className="forge-typography--heading3" id="attachment-title" data-testid="attachment-title">
          {I18n.t('title', { scope })}
        </h1>

        {!isEmpty(multiHierarchyTable) && (
          <ForgeInlineMessage
            theme="info"
            data-testid="multi-tab-attachment-banner"
            className="multi-tab-content"
          >
            <ForgeIcon slot="icon" name="info"></ForgeIcon>
            <p>
              <span>
                <b>{joinTableNames(multiHierarchyTable)}</b>
              </span>{' '}
              {/* Safe: translation content only, no user input */}
              <span
                dangerouslySetInnerHTML={{
                  __html: I18n.t('multi_tabs_banner', {
                    scope,
                    includeLabel:
                      multiHierarchyTable.length > 1
                        ? I18n.t('include', { scope })
                        : I18n.t('includes', { scope }),
                  }),
                }}
              ></span>
            </p>
          </ForgeInlineMessage>
        )}

        <div className="select-section">
          {attachmentConfigs.map((config, index) => (
            <div key={`${config.value}-${index}`}>{attachmentOptions(index, attachmentConfigs.length)}</div>
          ))}
        </div>

        <ForgeButton
          data-testid="add-attachment-btn"
          className="add-attachment"
          onClick={handleAddAttachment}
        >
          <ForgeIcon name="add" />
          <span>{I18n.t('add', { scope })}</span>
        </ForgeButton>
      </div>
    </div>
  );
};

export default AttachmentSection;
